import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFeatureOptIn } from '../../services/features';
import { lire, ecrire } from './annoncesApi';
import CriteresEditeur, { CRITERES_DEPART, nettoyerCriteres, useRegistreCriteres } from './CriteresEditeur';

/**
 * « Publier sur LinkedIn » sur la fiche d'un besoin (demande du 12.10 :
 * « enlève publier sur mon fil, c'est plutôt publier sur LinkedIn »).
 *
 * C'est l'OFFRE D'EMPLOI, publiée par l'agent du moteur de sourcing depuis le
 * compte Recruiter — pas un post sur le fil. Le serveur ouvre l'annonce du
 * besoin si elle n'existe pas (texte de la fiche de poste, client masqué),
 * puis confie l'offre à l'agent : POST /besoins/:id/offre-linkedin { publier }.
 * L'état vient de l'annonce ouverte du besoin (GET /annonces?besoin_id=&
 * ouvertes=1, champ `agent`) — l'écran ne le devine pas. Les candidatures
 * arrivent ensuite par la boîte candidatures, CV compris.
 *
 * Visible quand l'organisation a le module LinkedIn ET l'agent du moteur.
 * Une route absente (404/501) est annoncée, rien n'est écrit.
 *
 * Les CRITÈRES se choisissent ici, avant de publier (13.10 : « il ne me
 * demande pas les critères, comme le permis de travail suisse ») : ceux de
 * l'annonce ouverte, sinon l'autorisation de travail en Suisse pour commencer.
 * Ils deviennent les questions de présélection de LinkedIn.
 *
 * Le panneau s'ouvre PAR-DESSUS la page (portail, position fixe), comme les
 * menus d'ErpiaSelect : l'en-tête du besoin coupe ce qui dépasse de son cadre
 * (overflow hidden, pour son liseré), et le panneau y était caché sous les
 * chiffres du besoin (vécu 13.10 : « pourquoi j'ai le menu caché ? »).
 */
const BI = ({ icon, className }) => <i className={`bi bi-${icon}${className ? ` ${className}` : ''}`} aria-hidden="true" />;
const ETATS = {
  brouillon: { libelle: 'Remplie à blanc', ton: 'repeche' },
  bloquee: { libelle: 'Bloquée', ton: 'refuse' },
  erreur: { libelle: 'Pas aboutie', ton: 'refuse' },
};

export default function PublierOffreLinkedIn({ besoin, showNotification }) {
  const moduleLinkedin = useFeatureOptIn('linkedin_publication');
  const agent = useFeatureOptIn('sourcing_linkedin');
  const [annonce, setAnnonce] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [aBlanc, setABlanc] = useState(false);
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [absent, setAbsent] = useState(false);
  const [place, setPlace] = useState(null);
  const [criteres, setCriteres] = useState(CRITERES_DEPART);
  const { registre, absent: sansRegistre } = useRegistreCriteres(ouvert);
  const boutonRef = useRef(null);
  const panneauRef = useRef(null);
  const id = besoin?.id;

  const relire = useCallback(async () => {
    if (!id) return;
    const j = await lire(`/annonces?besoin_id=${id}&ouvertes=1`);
    if (!j.error && !j.absent) setAnnonce((j.annonces || [])[0] || null);
  }, [id]);
  useEffect(() => { if (moduleLinkedin && agent) relire(); }, [moduleLinkedin, agent, relire]);
  // Tant que l'agent travaille, l'état se relit toutes les 20 secondes.
  const statut = annonce?.agent?.statut || null;
  useEffect(() => {
    if (statut !== 'demandee') return undefined;
    const t = setInterval(relire, 20000);
    return () => clearInterval(t);
  }, [statut, relire]);
  // Sous le bouton, aligné sur son bord droit, gardé dans l'écran. Un bouton
  // sorti de l'écran (défilement) ferme le panneau plutôt que de le laisser
  // flotter seul.
  const positionner = useCallback(() => {
    const r = boutonRef.current?.getBoundingClientRect();
    if (!r) return;
    if (r.bottom < 0 || r.top > window.innerHeight) { setOuvert(false); return; }
    const largeur = Math.min(460, window.innerWidth - 16);
    setPlace({
      position: 'fixed',
      top: r.bottom + 6,
      left: Math.max(8, Math.min(r.right - largeur, window.innerWidth - largeur - 8)),
      width: largeur,
      maxHeight: Math.max(220, window.innerHeight - r.bottom - 18),
    });
  }, []);
  useLayoutEffect(() => { if (ouvert) positionner(); }, [ouvert, positionner]);
  useEffect(() => {
    if (!ouvert) return undefined;
    const echap = (e) => { if (e.key === 'Escape') { setOuvert(false); boutonRef.current?.focus(); } };
    // En capture, comme ErpiaSelect : un autre bouton qui coupe la propagation
    // ne doit pas laisser ce panneau ouvert.
    const dehors = (e) => {
      if (!boutonRef.current?.contains(e.target) && !panneauRef.current?.contains(e.target)) setOuvert(false);
    };
    window.addEventListener('keydown', echap);
    document.addEventListener('mousedown', dehors, true);
    window.addEventListener('resize', positionner);
    window.addEventListener('scroll', positionner, true);
    return () => {
      window.removeEventListener('keydown', echap);
      document.removeEventListener('mousedown', dehors, true);
      window.removeEventListener('resize', positionner);
      window.removeEventListener('scroll', positionner, true);
    };
  }, [ouvert, positionner]);

  if (!moduleLinkedin || !agent || !id) return null;
  const ag = annonce?.agent || null;
  const url = annonce?.offre_linkedin?.url || null;

  // Publiée : le bouton mène à l'offre.
  if (statut === 'publiee' && url) {
    return (
      <a className="bd-btn" href={url} target="_blank" rel="noreferrer" title={ag?.raison || "Voir l'offre sur LinkedIn"}>
        <BI icon="linkedin" /> Sur LinkedIn <BI icon="box-arrow-up-right" />
      </a>
    );
  }
  const enCours = statut === 'demandee';
  const etat = statut ? ETATS[statut] : null;

  const publier = async () => {
    setOccupe(true); setErreur(null);
    const j = await ecrire('POST', `/besoins/${id}/offre-linkedin`, { publier: !aBlanc, ...(registre ? { criteres: nettoyerCriteres(criteres) } : {}) });
    setOccupe(false);
    if (j.absent) { setAbsent(true); return; }
    if (j.error) { setErreur(j.error); return; }
    // Fiche de poste encore en brouillon de Leman : rien n'est parti, la
    // demande attend la validation (le serveur le dit, on le relaie en info).
    showNotification?.(j.message, j.en_attente ? 'info' : 'success');
    setOuvert(false);
    relire();
  };

  return (
    <span className="lk-offre">
      <button ref={boutonRef} type="button" className="bd-btn" disabled={enCours} aria-expanded={ouvert} aria-haspopup="dialog"
        title={enCours ? "L'agent publie l'offre : l'état se met à jour tout seul." : (ag?.raison || undefined)}
        onClick={() => {
          // À l'ouverture : les critères de l'annonce ouverte, sinon le départ.
          if (!ouvert) setCriteres(annonce ? (annonce.criteres || []) : CRITERES_DEPART);
          setOuvert(o => !o);
        }}>
        <BI icon={enCours ? 'arrow-repeat' : 'linkedin'} className={enCours ? 'ann-tourne' : ''} />
        {enCours ? 'LinkedIn : publication en cours' : 'Publier sur LinkedIn'}
        {etat && <span className={`ann-pastille ann-pastille--${etat.ton}`}>{etat.libelle}</span>}
      </button>
      {ouvert && place && typeof document !== 'undefined' && createPortal(
        <div ref={panneauRef} className="lk-offre__panneau" style={place} role="dialog" aria-label="Publier l'offre sur LinkedIn">
          {absent ? <p className="ann-texte">La publication sur LinkedIn arrive côté serveur : rien n'est encore branché.</p> : (
            <>
              {ag?.raison && (statut === 'bloquee' || statut === 'erreur') && <p className="ann-cand__motif">{ag.raison}</p>}
              {!sansRegistre && (
                <>
                  <h3 className="ann-sous-titre">Critères éliminatoires</h3>
                  <CriteresEditeur valeur={criteres} onChange={setCriteres} registre={registre} lieuDefaut={besoin?.localisation || ''} />
                </>
              )}
              <label className="ann-case ann-case--petit">
                <input type="checkbox" checked={aBlanc} onChange={e => setABlanc(e.target.checked)} /> Passage à blanc : remplir sans publier
              </label>
              {erreur && <p className="ann-erreur" role="alert">{erreur}</p>}
              <div className="lk-offre__actions">
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setOuvert(false)}>Annuler</button>
                <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" disabled={occupe} onClick={publier}>
                  <BI icon={occupe ? 'arrow-repeat' : 'send'} className={occupe ? 'ann-tourne' : ''} /> {aBlanc ? 'Remplir à blanc' : 'Publier sur LinkedIn'}
                </button>
              </div>
            </>
          )}
        </div>,
        document.body,
      )}
    </span>
  );
}
